import React, { useState } from "react";
import { AlertTriangle, Pencil, Trash2 } from "lucide-react";
import DrawingEvidenceImage from "./DrawingEvidenceImage";

const timeLabel = (iso) => (iso ? new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "");

// The saved drawing under the question (with Edit drawing and Remove), plus
// any unsaved draft left open on this or another device. A draft is never
// shown as the answer.
const DrawingAnswerSection = ({ attemptId, questionId, drawing, locked }) => {
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const { summary, busy, notice, padOpen } = drawing;
  const hasDraft = Boolean(summary?.draft || summary?.editing) && !padOpen;

  return (
    <>
      {notice && (
        <p className={`eq-drawing-notice is-${notice.tone || "info"}`} role="status">
          <span>{notice.text}</span>
          <button type="button" className="eq-assess-link-button" onClick={drawing.onDismissNotice}>Dismiss</button>
        </p>
      )}

      {hasDraft && (
        <div className="eq-assess-conflict eq-drawing-draft" role="alert">
          <AlertTriangle size={20} aria-hidden="true" />
          <div>
            <strong>
              {summary.draft
                ? `You have an unsaved drawing${summary.draft.acceptedAt ? ` (draft saved at ${timeLabel(summary.draft.acceptedAt)})` : ""}.`
                : "This drawing is open in another window or on another device."}
            </strong>
            <p>
              {summary.draft
                ? "It isn't your answer until you save it. If time runs out first, this draft may be submitted automatically."
                : "Open it here to continue, or discard the changes made there."}
            </p>
            {confirmDiscard ? (
              <div className="eq-assess-conflict-actions">
                <span>Discard the unsaved drawing? Your saved drawing (if any) stays.</span>
                <button type="button" className="eq-assess-secondary" disabled={Boolean(busy)}
                  onClick={() => { setConfirmDiscard(false); drawing.onDiscardDraft(); }}>Discard draft</button>
                <button type="button" className="eq-assess-secondary" onClick={() => setConfirmDiscard(false)}>Keep it</button>
              </div>
            ) : (
              <div className="eq-assess-conflict-actions">
                <button type="button" className="eq-assess-secondary" onClick={drawing.onOpen} disabled={locked || Boolean(busy)}>
                  {summary.draft ? "Resume drawing" : "Open here"}
                </button>
                <button type="button" className="eq-assess-secondary" onClick={() => setConfirmDiscard(true)} disabled={Boolean(busy)}>
                  {busy === "discarding" ? "Discarding…" : "Discard changes"}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {summary?.hasDrawing && (
        <section className="eq-drawing-preview" aria-label="Your drawing">
          <div className="eq-drawing-preview-head">
            <h3>Your drawing</h3>
            <div className="eq-drawing-preview-actions">
              <button type="button" className="eq-assess-link-button" onClick={drawing.onOpen} disabled={locked || Boolean(busy) || hasDraft}>
                <Pencil size={16} aria-hidden="true" />Edit drawing
              </button>
              <button type="button" className="eq-assess-link-button is-danger" onClick={() => setConfirmRemove(true)}
                disabled={locked || Boolean(busy) || hasDraft}>
                <Trash2 size={16} aria-hidden="true" />Remove
              </button>
            </div>
          </div>
          {confirmRemove && (
            <div className="eq-drawing-confirm" role="alert">
              <span>Remove this drawing from your answer?</span>
              <button type="button" className="eq-assess-secondary" onClick={() => { setConfirmRemove(false); drawing.onRemove(); }}>
                Remove drawing
              </button>
              <button type="button" className="eq-assess-secondary" onClick={() => setConfirmRemove(false)}>Keep it</button>
            </div>
          )}
          <div className="eq-drawing-preview-frame">
            <DrawingEvidenceImage
              attemptId={attemptId}
              questionId={questionId}
              version="committed"
              revisionKey={summary.revision}
              alt="Your saved drawing"
            />
          </div>
          {busy === "removing" && <p className="eq-drawing-image-status" role="status">Removing…</p>}
        </section>
      )}
    </>
  );
};

export default DrawingAnswerSection;
